/**
 * Overdue Invoice Detection Workflow
 *
 * This workflow monitors and alerts about overdue invoices:
 * 1. Runs daily to scan for overdue invoices
 * 2. Classifies risk level based on days overdue and amount
 * 3. Sends prioritized notifications to user
 */

import type { Workflow } from './types';

// =============================================================================
// WORKFLOW DEFINITION
// =============================================================================

export const overdueDetectionWorkflow: Workflow = {
  id: 'overdue-detection',
  name: 'Rilevamento Fatture Scadute',
  description: 'Monitora le fatture non pagate scadute e classifica il rischio',
  trigger: {
    type: 'schedule',
    config: {
      // Run daily at 10 AM
      cron: '0 10 * * *',
      timezone: 'Europe/Rome',
    },
  },
  steps: [
    {
      id: 'scan_invoices',
      name: 'Scansiona Fatture',
      type: 'custom',
      config: {
        // Find overdue unpaid invoices
        filterStatus: 'unpaid',
        filterOverdue: true,
        includePartiallyPaid: true,
        // Group by client
        groupByClient: true,
      },
      next: ['classify_risk'],
    },
    {
      id: 'classify_risk',
      name: 'Classifica Rischio',
      type: 'ai_analyze',
      config: {
        prompt: `Classify these overdue invoices by risk level.

For each invoice, consider:
- Days overdue (severity increases with time)
- Invoice amount (higher amounts = higher risk)
- Client payment history
- Total outstanding balance for client

Risk levels:
- CRITICAL: >60 days or amount >5000€
- HIGH: 30-60 days or amount 2000-5000€
- MEDIUM: 15-30 days or amount 500-2000€
- LOW: <15 days or amount <500€

Respond in JSON:
{
  "overdueInvoices": [
    {
      "invoiceId": "...",
      "clientName": "...",
      "amount": 0.00,
      "daysOverdue": 0,
      "riskLevel": "critical|high|medium|low",
      "suggestedAction": "Recommended next step"
    }
  ],
  "summary": {
    "totalOverdue": 0.00,
    "criticalCount": 0,
    "highCount": 0
  }
}`,
      },
      next: ['send_notification'],
    },
    {
      id: 'send_notification',
      name: 'Invia Notifica',
      type: 'send_notification',
      config: {
        type: 'in_app',
        message: 'Trovate {totalCount} fatture scadute per un totale di {totalAmount}€.',
        priority: 'high',
        groupByRisk: true,
        includeActions: true,
      },
    },
  ],
  enabled: true,
  version: 1,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Calculate risk score for overdue invoice
 */
export function calculateRiskScore(daysOverdue: number, amount: number): number {
  const timeScore = Math.min(100, daysOverdue * 1.5);
  const amountScore = Math.min(100, (amount / 1000) * 10);

  // Weighted combination: 60% time, 40% amount
  return timeScore * 0.6 + amountScore * 0.4;
}
